import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { Box, Hidden, withStyles } from '@material-ui/core';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import { makeStyles } from '@material-ui/core/styles';

import { subscriptionCurrentSelector, subscriptionPlansSelector } from 'state/subscription/selectors';

import { Text, TextTypography } from 'components/Text/Text';
import { ReactComponent as CheckCircle } from 'assets/icons/status/check-circle--16-for-subs.svg';
import { ReactComponent as ClockCircle } from 'assets/icons/status/times-circle--16-grey.svg';

import { ShopTypeName } from 'types';
import { Icon } from 'components/Icon/Icon';
import { Button, ButtonColor } from 'components/Button/Button';
import { makeSubscriptionChangeRateDialogAction } from 'state/subscription/actions';
import { useAction } from 'core/store/store';
import { ReactComponent as ExpandMoreIcon } from 'assets/icons/main-16/chevron-down--16.svg';
import { LargeButton } from 'components/LargeButton/LargeButton';
import i18n from '../../../../i18n';
import { authIsoCodeSelector } from '../../../state/auth/selectors';

const Accordion = withStyles({
    root: {
        border: 'none',
        boxShadow: 'none',
        '&:not(:last-child)': {
            borderBottom: 0,
        },
        '&:before': {
            display: 'none',
        },
        '&$expanded': {
            width: '100%',
        },
    },
    expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
    root: {
        backgroundColor: '#fff',
        border: 'none',
        minHeight: 38,
        '&$expanded': {
            minHeight: 38,
        },
    },
    content: {
        textAlign: 'center',
        display: 'block',
        '&$expanded': {
            margin: '12px 0',
        },
    },
    expanded: {
        backgroundColor: '#f3f3f4',
    },
})(MuiAccordionSummary);

const AccordionDetails = withStyles(theme => ({
    root: {
        padding: 0,
    },
}))(MuiAccordionDetails);

const useStyles = makeStyles(theme => ({
    profile: {
        maxWidth: 800,
        height: '100%',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.up(640)]: {
            marginTop: 24,
            boxShadow: '0px 0px 50px rgba(15, 24, 37, 0.05)',
            borderRadius: 20,
        },
        [theme.breakpoints.up(1919)]: {
            maxWidth: 1280,
        },
    },
    rate: {
        maxWidth: 800,
        margin: 'auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.up(640)]: {
            marginTop: 24,
            boxShadow: '0px 0px 50px rgba(15, 24, 37, 0.05)',
            borderRadius: 20,
        },
        [theme.breakpoints.up(1919)]: {
            maxWidth: 1280,
        },
    },
    header: {
        display: 'flex',
        padding: theme.spacing(2.5),
        borderBottom: `1px solid ${theme.palette.brandPrimary.accent}`,
    },
    title: {
        paddingRight: theme.spacing(3),
        width: '100%',
    },
    contents: {
        flex: 1,
        borderBottom: `1px solid ${theme.palette.brandPrimary.accent}`,
        [theme.breakpoints.up(640)]: {
            borderBottom: 'none',
        },
    },
    main: {
        borderTop: '1px solid #ddd',
    },
    isOpenSwitch: {
        padding: theme.spacing(2.5),
    },
    headerRow: {
        background: theme.palette.brandPrimary.lightest,
    },
    tableRow: {
        '&:nth-child(2n)': {
            background: theme.palette.brandPrimary.lightest,
        },
    },
    headerCell: {
        border: `1px solid ${theme.palette.brandPrimary.lightest}`,
        '&:first-child': {
            borderLeft: 'none',
        },
        '&:last-child': {
            borderRight: 'none',
        },
    },
    rateCard: {
        backgroundColor: '#fff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        boxShadow: '0px 0px 50px rgba(15, 24, 37, 0.05)',
        borderRadius: 20,
        margin: 24,
    },
}));

const RATE_ROWS = [
    'vendor:userMenu.subscription.rows.personalSite',
    'vendor:userMenu.subscription.rows.catalog',
    'vendor:userMenu.subscription.rows.qr',
    'vendor:userMenu.subscription.rows.optimization',
    'vendor:userMenu.subscription.rows.instagram',
    'vendor:userMenu.subscription.rows.mapPoint',
    'vendor:userMenu.subscription.rows.feedPosition',
    'vendor:userMenu.subscription.rows.onlineOrders',
    'vendor:userMenu.subscription.rows.onlinePayments',
    'vendor:userMenu.subscription.rows.sales',
    'vendor:userMenu.subscription.rows.delivery',
];

const BASIC_RATE = 5;
const PRIORITY_RATE = 7;

export interface VendorSubscriptionRateProps {
    handleOpenBasicDialog?: () => void;
    isVendorPage?: boolean;
}

export const VendorSubscriptionRate: FC<VendorSubscriptionRateProps> = ({
    handleOpenBasicDialog,
    isVendorPage = true,
}) => {
    const action = useAction();
    const classes = useStyles();
    const currentSubscription = useSelector(subscriptionCurrentSelector);
    const subscriptionPlans = useSelector(subscriptionPlansSelector);
    const { t } = i18n.useTranslation();

    const nextSubscriptionPlan = subscriptionPlans?.find(
        el => el.subscriptionPlanId === currentSubscription?.nextSubscriptionPlanId,
    );
    const isBasic = !currentSubscription?.active;
    const isShop =
        !isBasic &&
        ((nextSubscriptionPlan && nextSubscriptionPlan?.name === ShopTypeName.ONLINE_SHOP) ||
            (!nextSubscriptionPlan && currentSubscription?.subscriptionPlan?.name === ShopTypeName.ONLINE_SHOP));
    const isPriority =
        !isBasic &&
        ((nextSubscriptionPlan && nextSubscriptionPlan?.name === ShopTypeName.PRIORITY) ||
            (!nextSubscriptionPlan && currentSubscription?.subscriptionPlan?.name === ShopTypeName.PRIORITY));

    const handleOpenOnlineShopDialog = () => action(makeSubscriptionChangeRateDialogAction, ShopTypeName.ONLINE_SHOP);
    const handleOpenPriorityDialog = () => action(makeSubscriptionChangeRateDialogAction, ShopTypeName.PRIORITY);

    return (
        <div className={classes.rate}>
            <Box m={3}>
                <Text typography={TextTypography.TITLE_ACCENT}>{t('vendor:userMenu.subscription.rates')}</Text>
            </Box>
            <Hidden xsDown>
                <table cellSpacing={0}>
                    <thead>
                        <tr className={classes.headerRow}>
                            <th align="left" className={classes.headerCell} scope="col">
                                <Box p={1} pl={2}>
                                    <Text typography={TextTypography.BODY_ACCENT}>
                                        {t('vendor:userMenu.subscription.functionality')}
                                    </Text>
                                </Box>
                            </th>
                            <th className={classes.headerCell} scope="col">
                                <Text typography={TextTypography.BODY_ACCENT}>
                                    {t('vendor:userMenu.subscription.basicRate')}
                                </Text>
                                {isVendorPage && (
                                <Box m={2} mt={1}>
                                    <Button
                                        disabled={isBasic}
                                        color={ButtonColor.DARK}
                                        onClick={handleOpenBasicDialog}
                                    >
                                        {isBasic
                                            ? t('vendor:userMenu.subscription.connected')
                                            : t('vendor:userMenu.subscription.connect')}
                                    </Button>
                                </Box>
                                )}
                                {/*<Text typography={TextTypography.BODY}>{t('vendor:userMenu.subscription.free')}</Text>*/}
                            </th>
                            <th className={classes.headerCell} scope="col">
                                <Text typography={TextTypography.BODY_ACCENT}>
                                    {t('vendor:userMenu.subscription.priority')}
                                </Text>
                                {isVendorPage && (
                                <Box m={2} mt={1}>
                                    <Button
                                        disabled={isPriority}
                                        color={ButtonColor.DARK}
                                        onClick={handleOpenPriorityDialog}
                                    >
                                        {isPriority
                                            ? t('vendor:userMenu.subscription.connected')
                                            : t('vendor:userMenu.subscription.connect')}
                                    </Button>
                                </Box>
                                )}
                                {/*<Text typography={TextTypography.BODY}>{promoPrice(isoCode)} </Text>*/}
                            </th>
                            <th className={classes.headerCell} scope="col">
                                <Text typography={TextTypography.BODY_ACCENT}>
                                    {t('vendor:userMenu.subscription.onlineShop')}
                                </Text>
                                {isVendorPage && (
                                 <Box m={2} mt={1}>
                                    <Button
                                        disabled={isShop}
                                        color={ButtonColor.DARK}
                                        onClick={handleOpenOnlineShopDialog}
                                    >
                                        {isShop
                                            ? t('vendor:userMenu.subscription.connected')
                                            : t('vendor:userMenu.subscription.connect')}
                                    </Button>
                                </Box>
                                )}
                                {/*<Text typography={TextTypography.BODY}>{subscriptionPrice(isoCode)}</Text>*/}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {RATE_ROWS.map((el, index) => (
                            <tr key={index} className={classes.tableRow}>
                                <th align="left">
                                    <Box p={1} pl={2}>
                                        <Text typography={TextTypography.DISPLAY_LIGHT_CHANGE}>{t(el)}</Text>
                                    </Box>
                                </th>
                                <td align="center">
                                    {index + 1 > BASIC_RATE ? (
                                        <Icon component={ClockCircle} svgSize={16} size={16} />
                                    ) : (
                                        <Icon component={CheckCircle} svgSize={16} size={16} />
                                    )}
                                </td>
                                <td align="center">
                                    {index + 1 > PRIORITY_RATE ? (
                                        <Icon component={ClockCircle} svgSize={16} size={16} />
                                    ) : (
                                        <Icon component={CheckCircle} svgSize={16} size={16} />
                                    )}
                                </td>
                                <td align="center">
                                    <Icon component={CheckCircle} svgSize={16} size={16} />
                                </td>
                            </tr>
                        ))}

                        {handleOpenBasicDialog && isVendorPage && (
                            <tr>
                                <th>&nbsp;</th>
                                <td align="center">
                                    <Box p={1} pb={3}>
                                        <Button
                                            disabled={isBasic}
                                            color={ButtonColor.DARK}
                                            onClick={handleOpenBasicDialog}
                                        >
                                            {isBasic
                                                ? t('vendor:userMenu.subscription.connected')
                                                : t('vendor:userMenu.subscription.connect')}
                                        </Button>
                                    </Box>
                                </td>
                                <td align="center">
                                    <Box p={1} pb={3}>
                                        <Button
                                            disabled={isPriority}
                                            color={ButtonColor.DARK}
                                            onClick={handleOpenPriorityDialog}
                                        >
                                            {isPriority
                                                ? t('vendor:userMenu.subscription.connected')
                                                : t('vendor:userMenu.subscription.connect')}
                                        </Button>
                                    </Box>
                                </td>
                                <td align="center">
                                    <Box p={1} pb={3}>
                                        <Button
                                            disabled={isShop}
                                            color={ButtonColor.DARK}
                                            onClick={handleOpenOnlineShopDialog}
                                        >
                                            {isShop
                                                ? t('vendor:userMenu.subscription.connected')
                                                : t('vendor:userMenu.subscription.connect')}
                                        </Button>
                                    </Box>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </Hidden>
            <Hidden smUp>
                <div className={classes.rateCard}>
                    <Box mt={3}>
                        <Text typography={TextTypography.LEAD_STRONG}>
                            {t('vendor:userMenu.subscription.onlineShop')}
                        </Text>
                    </Box>
                    {/* <Box mt={1} mb={1}>
                        <Text typography={TextTypography.PARA}>{subscriptionPrice(isoCode)}</Text>
                    </Box> */}
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                            style={{
                                display: 'flex',
                                padding: '0px 16px',
                                minHeight: 48,
                            }}
                        >
                            <Box alignContent="center">
                                <Text typography={TextTypography.BODY}>
                                    {t('vendor:userMenu.subscription.functionality')}
                                </Text>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <table width="100%" cellSpacing={0}>
                                {RATE_ROWS.map((el, index) => (
                                    <tr key={index} className={classes.tableRow}>
                                        <th align="left">
                                            <Box p={1} pl={3}>
                                                <Text typography={TextTypography.DISPLAY_LIGHT_CHANGE}>{t(el)}</Text>
                                            </Box>
                                        </th>
                                        <td align="center">
                                            <Box pr={3}>
                                                <Icon component={CheckCircle} svgSize={16} size={16} />
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                            </table>
                        </AccordionDetails>
                    </Accordion>
                    <Box p={isVendorPage ? 3 : 1} pt={2} width="100%">
                        {isVendorPage && (
                            <LargeButton disabled={isShop} onClick={handleOpenOnlineShopDialog}>
                                {isShop
                                    ? t('vendor:userMenu.subscription.connected')
                                    : t('vendor:userMenu.subscription.connect')}
                            </LargeButton>
                        )}
                    </Box>
                </div>
                <div className={classes.rateCard}>
                    <Box mt={3}>
                        <Text typography={TextTypography.LEAD_STRONG}>
                            {t('vendor:userMenu.subscription.priority')}
                        </Text>
                    </Box>
                    {/* <Box mt={1} mb={1}>
                        <Text typography={TextTypography.PARA}>{promoPrice(isoCode)}</Text>
                    </Box> */}
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                            style={{
                                display: 'flex',
                                padding: '0px 16px',
                                minHeight: 48,
                            }}
                        >
                            <Box alignContent="center">
                                <Text typography={TextTypography.BODY}>
                                    {t('vendor:userMenu.subscription.functionality')}
                                </Text>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <table width="100%" cellSpacing={0}>
                                {RATE_ROWS.map((el, index) => (
                                    <tr key={index} className={classes.tableRow}>
                                        <th align="left">
                                            <Box p={1} pl={3}>
                                                <Text typography={TextTypography.DISPLAY_LIGHT_CHANGE}>{t(el)}</Text>
                                            </Box>
                                        </th>
                                        <td align="center">
                                            <Box pr={3}>
                                                {index + 1 > PRIORITY_RATE ? (
                                                    <Icon component={ClockCircle} svgSize={16} size={16} />
                                                ) : (
                                                    <Icon component={CheckCircle} svgSize={16} size={16} />
                                                )}
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                            </table>
                        </AccordionDetails>
                    </Accordion>
                    <Box p={isVendorPage ? 3 : 1} pt={2} width="100%">
                        {isVendorPage && (
                            <LargeButton disabled={isPriority} onClick={handleOpenPriorityDialog}>
                                {isPriority
                                    ? t('vendor:userMenu.subscription.connected')
                                    : t('vendor:userMenu.subscription.connect')}
                            </LargeButton>
                        )}
                    </Box>
                </div>
                <div className={classes.rateCard}>
                    <Box mt={3}>
                        <Text typography={TextTypography.LEAD_STRONG}>
                            {t('vendor:userMenu.subscription.basicRate')}
                        </Text>
                    </Box>
                    {/* <Box mt={1} mb={1}>
                        <Text typography={TextTypography.PARA}>{t('vendor:userMenu.subscription.free')}</Text>
                    </Box> */}
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                            style={{
                                display: 'flex',
                                padding: '0px 16px',
                                minHeight: 48,
                            }}
                        >
                            <Box alignContent="center">
                                <Text typography={TextTypography.BODY}>
                                    {t('vendor:userMenu.subscription.functionality')}
                                </Text>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <table width="100%" cellSpacing={0}>
                                {RATE_ROWS.map((el, index) => (
                                    <tr key={index} className={classes.tableRow}>
                                        <th align="left">
                                            <Box p={1} pl={3}>
                                                <Text typography={TextTypography.DISPLAY_LIGHT_CHANGE}>{t(el)}</Text>
                                            </Box>
                                        </th>
                                        <td align="center">
                                            <Box pr={3}>
                                                {index + 1 > BASIC_RATE ? (
                                                    <Icon component={ClockCircle} svgSize={16} size={16} />
                                                ) : (
                                                    <Icon component={CheckCircle} svgSize={16} size={16} />
                                                )}
                                            </Box>
                                        </td>
                                    </tr>
                                ))}
                            </table>
                        </AccordionDetails>
                    </Accordion>
                    <Box p={isVendorPage ? 3 : 1} pt={2} width="100%">
                        {isVendorPage && (
                            <LargeButton disabled={isBasic} onClick={handleOpenBasicDialog}>
                                {isBasic
                                    ? t('vendor:userMenu.subscription.connected')
                                    : t('vendor:userMenu.subscription.connect')}
                            </LargeButton>
                        )}
                    </Box>
                </div>
            </Hidden>
        </div>
    );
};
